import type { ContactWithEmployment, WorkplaceMarker, WorkplaceEmployee } from '../../types/workplaceOverlay'
import type { HouseholdMarker } from '../../types/turfMap'
import { ContactStatus } from '../../types/turfMap'
import type { HouseholdDto } from '../../types/turf'
import { WORKPLACE_COLORS } from '../../lib/mapConfig'

function isRevisitStatus(status: number): boolean {
  return status === ContactStatus.NoAnswer || status === ContactStatus.Callback
}

/**
 * Group contacts by their primary (or first active) workplace into WorkplaceMarkers.
 * Only includes employments that have a worksite with lat/lng coordinates.
 */
export function groupContactsByWorkplace(
  contacts: ContactWithEmployment[]
): WorkplaceMarker[] {
  const worksiteMap = new Map<string, {
    marker: Omit<WorkplaceMarker, 'employees' | 'employeeCount' | 'visitedCount' | 'needsRevisitCount'>
    employees: WorkplaceEmployee[]
  }>()

  for (const contact of contacts) {
    // Use primary employment first, then first employment with location
    const employment =
      contact.employments.find(e => e.isPrimary && e.worksiteLatitude != null) ??
      contact.employments.find(e => e.worksiteLatitude != null)

    if (!employment) continue

    const key = employment.worksiteId

    if (!worksiteMap.has(key)) {
      worksiteMap.set(key, {
        marker: {
          worksiteId: employment.worksiteId,
          worksiteLabel: employment.worksiteLabel,
          businessName: employment.businessName,
          longitude: employment.worksiteLongitude!,
          latitude: employment.worksiteLatitude!,
          streetAddress: employment.worksiteStreetAddress ?? '',
          city: employment.worksiteCity,
          state: employment.worksiteState,
          zip: employment.worksiteZip,
        },
        employees: [],
      })
    }

    worksiteMap.get(key)!.employees.push({
      contactId: contact.id,
      contactName: `${contact.firstName} ${contact.lastName}`,
      position: employment.position,
      unionName: employment.unionName,
      unionLocalNumber: employment.unionLocalNumber,
      isVisited: contact.isVisited,
      contactStatus: contact.contactStatus ?? 0,
    })
  }

  return Array.from(worksiteMap.values()).map(({ marker, employees }) => ({
    ...marker,
    employees,
    employeeCount: employees.length,
    visitedCount: employees.filter(e => e.isVisited).length,
    needsRevisitCount: employees.filter(e => e.isVisited && isRevisitStatus(e.contactStatus)).length,
  }))
}

/**
 * Split contacts into those with a workplace location and those without.
 * Used for hybrid mode: workplace contacts get blue dots, others get red household dots.
 */
export function splitByEmploymentStatus(contacts: ContactWithEmployment[]): {
  withWork: ContactWithEmployment[]
  withoutWork: ContactWithEmployment[]
} {
  const withWork: ContactWithEmployment[] = []
  const withoutWork: ContactWithEmployment[] = []

  for (const contact of contacts) {
    const hasWorkLocation = contact.employments.some(e => e.worksiteLatitude != null)
    if (hasWorkLocation) {
      withWork.push(contact)
    } else {
      withoutWork.push(contact)
    }
  }

  return { withWork, withoutWork }
}

/**
 * Convert contacts without employment into HouseholdMarkers for hybrid mode.
 */
export function contactsToHouseholdMarkers(
  contacts: ContactWithEmployment[]
): HouseholdMarker[] {
  const householdMap = new Map<string, HouseholdMarker>()

  for (const contact of contacts) {
    if (contact.homeLatitude == null || contact.homeLongitude == null) continue

    const key = `${(contact.streetAddress ?? '').trim().toUpperCase()}|${contact.zip ?? ''}`

    if (!householdMap.has(key)) {
      householdMap.set(key, {
        householdKey: key,
        longitude: contact.homeLongitude,
        latitude: contact.homeLatitude,
        streetAddress: contact.streetAddress ?? '',
        city: '',
        state: null,
        zip: contact.zip ?? '',
        contactCount: 0,
        visitedCount: 0,
        needsRevisitCount: 0,
        contacts: [],
      })
    }

    const household = householdMap.get(key)!
    household.contactCount++
    if (contact.isVisited) household.visitedCount++
    const status = contact.contactStatus ?? 0
    if (contact.isVisited && isRevisitStatus(status)) household.needsRevisitCount++
    household.contacts.push({
      contactId: contact.id,
      contactName: `${contact.firstName} ${contact.lastName}`,
      walkOrder: 0,
      isVisited: contact.isVisited,
      contactStatus: status,
      turfId: '',
      turfIndex: 0,
    })
  }

  return Array.from(householdMap.values())
}

/**
 * Overlay turf-specific isVisited status from household data onto employment contacts.
 * The contacts-with-employment endpoint doesn't know about turf context, so isVisited
 * defaults to false. This function merges the correct isVisited from TurfMember-backed
 * household data (which tracks per-turf visitation).
 */
export function applyTurfVisitedStatus(
  contacts: ContactWithEmployment[],
  households: HouseholdDto[] | undefined
): ContactWithEmployment[] {
  if (!households?.length) return contacts

  // Build contactId -> { isVisited, contactStatus } lookup from household data
  const turfLookup = new Map<string, { isVisited: boolean; contactStatus: number }>()
  for (const h of households) {
    for (const c of h.contacts) {
      turfLookup.set(c.contactId, { isVisited: c.isVisited, contactStatus: c.contactStatus })
    }
  }

  return contacts.map(c => {
    const turf = turfLookup.get(c.id)
    if (!turf) return c
    const needsUpdate = turf.isVisited !== c.isVisited || turf.contactStatus !== (c.contactStatus ?? 0)
    if (needsUpdate) {
      return { ...c, isVisited: turf.isVisited, contactStatus: turf.contactStatus }
    }
    return c
  })
}

/**
 * Convert WorkplaceMarkers to GeoJSON FeatureCollection for MapLibre rendering.
 */
export function workplacesToGeoJson(
  workplaces: WorkplaceMarker[]
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: workplaces.map(wp => {
      const allVisited = wp.visitedCount === wp.employeeCount
      const noneVisited = wp.visitedCount === 0
      const hasRevisits = wp.needsRevisitCount > 0

      let color: string
      if (allVisited && !hasRevisits) color = WORKPLACE_COLORS.all_visited
      else if (noneVisited) color = WORKPLACE_COLORS.none_visited
      else if (hasRevisits) color = WORKPLACE_COLORS.needs_revisit
      else color = WORKPLACE_COLORS.partial_visited

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [wp.longitude, wp.latitude],
        },
        properties: {
          worksiteId: wp.worksiteId,
          worksiteLabel: wp.worksiteLabel,
          businessName: wp.businessName ?? '',
          streetAddress: wp.streetAddress,
          city: wp.city ?? '',
          state: wp.state ?? '',
          zip: wp.zip ?? '',
          employeeCount: wp.employeeCount,
          visitedCount: wp.visitedCount,
          color,
        },
      }
    }),
  }
}
